import { PrayerDisplay, LayoutType } from '../types/prayer';
import { DebugPanel } from './DebugPanel';

interface AllPrayersListProps {
  prayers: PrayerDisplay[];
  nextPrayer: string;
  timeRemaining: number;
  currentTime: string;
  layout: LayoutType;
  onLayoutChange: (layout: LayoutType) => void;
  location: string;
  timeUntilNext?: string;
  maghribBoomEnabled?: boolean;
  onToggleBoom?: () => void;
}

export function AllPrayersList({ prayers, nextPrayer, timeRemaining, currentTime, layout, onLayoutChange, location, timeUntilNext, maghribBoomEnabled = false, onToggleBoom }: AllPrayersListProps) {
  // Function to remove AM/PM from time string
  const removeAmPm = (time: string): string => {
    return time.replace(/\s*(am|pm|AM|PM)\s*$/i, '').trim();
  };

  return (
    <div className="h-screen w-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 flex flex-col overflow-hidden relative">
      {/* Fixed Timer in top right */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50">
        <div className="bg-yellow-400 text-gray-900 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-lg">
          <div className="text-sm sm:text-base md:text-lg font-bold">
            {timeRemaining}
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col items-center pt-4 sm:pt-6 md:pt-8 pb-2 sm:pb-4 px-2 sm:px-4">
        <div className="text-center mb-2 sm:mb-3">
          <div
            className="font-black text-white drop-shadow-2xl tracking-wider leading-none"
            style={{ fontSize: 'clamp(3rem, 15vw, 22rem)' }}
          >
            {currentTime}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-white text-xs sm:text-sm mb-1 sm:mb-2">
          <span className="font-semibold">{location}</span>
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto flex-1 flex flex-col justify-center gap-1 sm:gap-2 px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden">
        {prayers.map((prayer) => {
          const isNext = prayer.key === nextPrayer;
          const prayerEmojis: Record<string, string> = {
            fajr: '🌅',
            sunrise: '☀️',
            zuhr: '🌞',
            asr: '🌤️',
            maghrib: '🌆',
            isha: '🌙'
          };
          return (
            <div
              key={prayer.key}
              className={`flex-1 px-2 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 lg:px-8 lg:py-5 rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg transition-all flex items-center min-h-0 ${
                isNext
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white'
                  : 'bg-white text-gray-800'
              }`}
            >
              <div className="flex items-center justify-between w-full min-w-0">
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 xl:gap-8 flex-shrink min-w-0">
                  <div
                    className="flex-shrink-0"
                    style={{
                      fontSize: 'clamp(2rem, 5vw, 8rem)',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                    }}
                  >
                    {prayerEmojis[prayer.key]}
                  </div>
                  <div
                    className={`font-black ${isNext ? 'text-white' : 'text-emerald-600'}`}
                    style={{
                      fontSize: 'clamp(2rem, 5vw, 8rem)',
                      filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))'
                    }}
                  >
                    {prayer.nameArabic}
                  </div>
                  <div
                    className={`font-black ${isNext ? 'text-emerald-50' : 'text-gray-700'}`}
                    style={{
                      fontSize: 'clamp(1.75rem, 4vw, 7rem)',
                      filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))'
                    }}
                  >
                    {prayer.name}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 sm:gap-2 flex-shrink-0 ml-2 sm:ml-4">
                  <div
                    className={`font-black ${isNext ? 'text-white' : 'text-gray-900'}`}
                    style={{
                      fontSize: 'clamp(2.5rem, 6vw, 9rem)',
                      filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))'
                    }}
                  >
                    {removeAmPm(prayer.time)}
                  </div>
                  {isNext && timeUntilNext && (
                    <div
                      className="font-black text-red-500 bg-white/90 px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-lg"
                      style={{
                        fontSize: 'clamp(1.5rem, 4vw, 7rem)',
                        filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.4))'
                      }}
                    >
                      {timeUntilNext}
                    </div>
                  )}
                  {prayer.key === 'maghrib' && onToggleBoom && (
                    <button
                      onClick={onToggleBoom}
                      className={`px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-lg font-bold text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl transition-all ${
                        maghribBoomEnabled
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                      }`}
                      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                    >
                      💥 Boom {maghribBoomEnabled ? 'ON' : 'OFF'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DebugPanel
        currentTime={currentTime}
        nextPrayerName={prayers.find(p => p.key === nextPrayer)?.name}
        nextPrayerTime={prayers.find(p => p.key === nextPrayer)?.time}
      />
    </div>
  );
}
